import { useEffect, useState } from "react";
import qrUrl from "../assets/appstore-qr.svg";
import { APP_STORE_URL } from "../data/appStore";
import "./ContributePopover.css";

// MVP contribute loop (Quill): the app is live, so this is the real path —
// install Minnea, enter the join code, photos join this album (owner approves
// each, existing collab-join). Universal-Link auto-apply is the deferred Forge
// enhancement; MVP = paste the code post-install.
// Look/behaviour per Iris (PLANS/WEB_READER_FOOTER_SPEC.md · Contribute popover).

export function ContributePopover({
  open,
  joinCode,
  onClose,
}: {
  open: boolean;
  joinCode: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  // Esc to dismiss (desktop).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(joinCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="contrib" role="dialog" aria-modal="true" aria-label="Add your photos">
      <div className="contrib__backdrop" onClick={onClose} />
      <div className="contrib__card">
        <button
          type="button"
          className="contrib__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <h2 className="contrib__title">Add your photos</h2>
        <p className="contrib__line">
          Install Minnea, enter this code, and your photos join this album.
        </p>

        <div className="contrib__row">
          {/* Desktop-only: QR to continue on phone */}
          <div className="contrib__qr">
            <img src={qrUrl} alt="" className="contrib__qr-img" />
            <span className="contrib__cap">
              Scan to continue
              <br />
              on your phone
            </span>
          </div>

          <div className="contrib__code-col">
            <span className="contrib__label">Your join code</span>
            <span className="contrib__code-value">{joinCode}</span>
            <button type="button" className="contrib__copy" onClick={copy}>
              {copied ? "Copied ✓" : "Copy"}
            </button>
            <span className="contrib__micro">The owner approves each photo.</span>
          </div>
        </div>

        <AppStoreBadge />
      </div>
    </div>
  );
}

/** "Download on the App Store" badge → App Store. NOTE: token-matched
 *  recreation; swap in Apple's official badge asset for guideline compliance. */
function AppStoreBadge() {
  return (
    <a
      className="contrib__badge"
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download Minnea on the App Store"
    >
      <svg viewBox="0 0 22 22" className="contrib__badge-logo" aria-hidden="true">
        <path
          fill="currentColor"
          d="M16.7 11.7c0-2 1.6-2.9 1.7-3-1-1.3-2.4-1.5-2.9-1.5-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.2 2-1.4 2.4-.4 6 1 8 .7 1 1.4 2 2.4 2 1 0 1.3-.6 2.5-.6 1.2 0 1.5.6 2.5.6s1.7-1 2.3-2c.7-1.1 1-2.2 1-2.3-.1 0-2-.8-2-3.2zM14.8 5.9c.5-.7.9-1.6.8-2.6-.8 0-1.8.5-2.4 1.2-.5.6-.9 1.5-.8 2.4.9.1 1.8-.4 2.4-1z"
        />
      </svg>
      <span className="contrib__badge-text">
        <span className="contrib__badge-small">Download on the</span>
        <span className="contrib__badge-big">App Store</span>
      </span>
    </a>
  );
}
